import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  FileSpreadsheet, 
  Upload, 
  Download, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Trash2,
  ArrowRight,
  RefreshCw,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const ExcelCleanerTab = () => {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState(''); // 'uploading', 'processing', 'done'
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Fetch job history on mount
  useEffect(() => {
    fetchJobHistory();
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [token]);

  const fetchJobHistory = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/excel-cleaner/jobs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobHistory(response.data.jobs || []);
    } catch (err) {
      console.error('Error fetching job history:', err);
    }
  };

  const pollJobStatus = useCallback(async (jobId) => {
    if (!token || !jobId) return;
    
    try {
      const response = await axios.get(`${API}/excel-cleaner/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const job = response.data;
      
      if (job.status === 'completed') {
        // Job completed!
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setProcessingStage('done');
        setLoading(false);
        setResult({
          message: `Excel limpiado: ${job.rows} filas, ${job.clean_columns} columnas`,
          rows: job.rows,
          clean_columns: job.clean_columns,
          original_columns: job.original_columns,
          modified_count: job.modified_count || 0,
          filename: job.filename,
          job_id: jobId
        });
        fetchJobHistory();
      } else if (job.status === 'error') {
        // Job failed
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setProcessingStage('');
        setLoading(false);
        setError(job.error || 'Error desconocido al procesar el archivo');
        setCurrentJobId(null);
      } else if (job.status === 'processing') {
        // Still processing
        setProcessingStage('processing');
      }
    } catch (err) {
      console.error('Error polling job status:', err);
    }
  }, [token]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      setError('Por favor selecciona un archivo Excel (.xlsx o .xls)');
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
    setFileSize(selectedFile.size);
    setResult(null);
    setError('');
    setUploadProgress(0);
    setProcessingStage('');
    setCurrentJobId(null);
  };

  const handleClean = async () => {
    if (!file) {
      setError('Selecciona un archivo primero');
      return;
    }

    if (!token) {
      setError('Sesión no válida. Por favor recarga la página e inicia sesión.');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setUploadProgress(0);
    setProcessingStage('uploading');

    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log(`Starting upload of ${file.name} (${formatFileSize(file.size)})`);

      // Upload the file - this returns immediately with a job_id
      const response = await axios.post(`${API}/excel-cleaner/upload`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          timeout: 300000, // 5 minutes for upload only
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
            if (percentCompleted === 100) {
              setProcessingStage('processing');
            }
          }
        }
      );

      console.log('Server response:', response.data);
      
      const jobId = response.data.job_id;
      setCurrentJobId(jobId);
      setProcessingStage('processing');

      // Start polling for job status
      pollIntervalRef.current = setInterval(() => {
        pollJobStatus(jobId);
      }, 2000); // Poll every 2 seconds

    } catch (err) {
      console.error('Error uploading Excel:', err);
      setProcessingStage('');
      setLoading(false);
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Error al subir el archivo. Intenta de nuevo.');
      } else if (err.response?.status === 413) {
        setError('El archivo es demasiado grande para subir. Máximo permitido: 100MB');
      } else if (err.response?.status === 401) {
        setError('Sesión expirada. Por favor recarga la página e inicia sesión de nuevo.');
      } else if (err.response?.status === 500) {
        setError(`Error del servidor: ${err.response?.data?.detail || 'Error interno'}`);
      } else if (!err.response) {
        setError(`Error de conexión: ${err.message || 'No se pudo conectar con el servidor'}`);
      } else {
        setError(err.response?.data?.detail || 'Error al subir el archivo');
      }
    }
  };

  const handleDownload = async (jobId = null) => {
    const downloadJobId = jobId || result?.job_id;
    if (!downloadJobId) return;

    try {
      const response = await axios.get(
        `${API}/excel-cleaner/job/${downloadJobId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 300000
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `catalogo_limpio_${downloadJobId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading:', err);
      setError('Error al descargar el archivo. Intenta de nuevo.');
    }
  };

  const handleDownloadModified = async (jobId = null) => {
    const downloadJobId = jobId || result?.job_id;
    if (!downloadJobId) return;

    try {
      const response = await axios.get(
        `${API}/excel-cleaner/job/${downloadJobId}/download-modified`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 300000
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sitios_modificados_72h_${downloadJobId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading modified:', err);
      if (err.response?.status === 404) {
        setError('No hay archivo de productos modificados disponible.');
      } else {
        setError('Error al descargar el archivo. Intenta de nuevo.');
      }
    }
  };

  const handleReset = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setFile(null);
    setFileName('');
    setFileSize(0);
    setResult(null);
    setError('');
    setUploadProgress(0);
    setProcessingStage('');
    setCurrentJobId(null);
    setLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!token) return;
    try {
      await axios.delete(`${API}/excel-cleaner/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJobHistory();
    } catch (err) {
      console.error('Error deleting job:', err);
    }
  };

  // Columns that will be removed
  const removedColumns = [
    'DA', 'PRECIO COMPRA OFERTA', 'PVP OFERTA', 'FECHA INICIO OFERTA', 
    'FECHA FIN OFERTA', 'MORE INFO', 'INFO PRICE', 'CONTINENT', 
    'FECHA CREACION', 'FECHA CAMBIO', 'SEMANA', 'PERMITE REDACCION', 
    'VALIDADO x ADMINISTRADOR', 'META', 'hsn_sac_code'
  ];

  // Columns that will be kept
  const keptColumns = [
    { excel: 'WEBSITE', app: 'website' },
    { excel: 'COUNTRY', app: 'country' },
    { excel: 'TOPIC', app: 'category' },
    { excel: 'CASINO', app: 'casino' },
    { excel: 'CBD', app: 'cbd' },
    { excel: 'GAMBLING', app: 'gambling' },
    { excel: 'TAG LABEL', app: 'tag_label' },
    { excel: 'PVP', app: 'pvp' },
    { excel: 'PC', app: 'pc' },
    { excel: 'DR', app: 'dr' },
    { excel: 'Visita Ahred', app: 'organic_traffic' },
    { excel: 'LANGUAGE', app: 'language' },
    { excel: 'PERIODICO, BLOG', app: 'site_type' },
    { excel: 'DF/NF/SP', app: 'link_type' },
    { excel: 'PREMIUM PRICE', app: 'premium_sale_price' },
    { excel: 'duration_link', app: 'link_duration' },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Limpiador de Catálogo Excel</h2>
        <p className="text-muted-foreground">
          Sube tu catálogo de productos y obtén un Excel limpio con solo las columnas necesarias para la app
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            {result.message}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Subir Catálogo
            </CardTitle>
            <CardDescription>
              Selecciona el archivo Excel con tu catálogo de productos (soporta archivos grandes)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".xlsx,.xls"
              className="hidden"
            />
            
            <div 
              onClick={() => !loading && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                loading ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-primary/50 hover:bg-muted/50'
              }`}
            >
              <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              {fileName ? (
                <div>
                  <p className="font-medium text-foreground">{fileName}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {formatFileSize(fileSize)} {!loading && '• Click para cambiar archivo'}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="font-medium text-foreground">Click para seleccionar archivo</p>
                  <p className="text-sm text-muted-foreground mt-1">Solo archivos .xlsx o .xls (sin límite de tamaño)</p>
                </div>
              )}
            </div>

            {/* Progress indicator */}
            {loading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {processingStage === 'uploading' && 'Subiendo archivo...'}
                    {processingStage === 'processing' && 'Procesando Excel en segundo plano...'}
                    {processingStage === 'done' && '¡Completado!'}
                  </span>
                  {processingStage === 'uploading' && (
                    <span className="font-medium">{uploadProgress}%</span>
                  )}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      processingStage === 'processing' ? 'bg-yellow-500 animate-pulse w-full' : 'bg-primary'
                    }`}
                    style={{ width: processingStage === 'processing' ? '100%' : `${uploadProgress}%` }}
                  ></div>
                </div>
                {processingStage === 'processing' && (
                  <div className="flex items-center gap-2 text-yellow-600 text-xs">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    <span>El procesamiento puede tardar varios minutos para archivos grandes. No cierres esta página.</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button 
                onClick={handleClean} 
                disabled={!file || loading}
                className="flex-1"
                data-testid="clean-excel-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {processingStage === 'uploading' ? 'Subiendo...' : 'Procesando...'}
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Limpiar Excel
                  </>
                )}
              </Button>
              
              {(file || loading) && (
                <Button variant="outline" onClick={handleReset} data-testid="reset-button">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Result Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Download className="h-5 w-5 text-primary" />
              Resultado
            </CardTitle>
            <CardDescription>
              Descarga el archivo limpio y los sitios modificados
            </CardDescription>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-foreground">{result.rows?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Filas</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-foreground">
                      {result.original_columns} <ArrowRight className="inline h-4 w-4" /> {result.clean_columns}
                    </p>
                    <p className="text-sm text-muted-foreground">Columnas</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center border border-orange-200">
                    <p className="text-2xl font-bold text-orange-600">{result.modified_count || 0}</p>
                    <p className="text-sm text-orange-600">Modificados 72h</p>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-700">
                    <strong>Se eliminaron {result.original_columns - result.clean_columns} columnas</strong> innecesarias
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button onClick={() => handleDownload()} className="w-full" size="lg" data-testid="download-clean-button">
                    <Download className="h-4 w-4 mr-2" />
                    Descargar Excel Limpio
                  </Button>
                  
                  {result.modified_count > 0 ? (
                    <Button 
                      onClick={() => handleDownloadModified()} 
                      className="w-full bg-orange-600 hover:bg-orange-700" 
                      size="lg"
                      data-testid="download-modified-button"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Descargar Modificados ({result.modified_count})
                    </Button>
                  ) : (
                    <Button 
                      disabled
                      variant="outline"
                      className="w-full" 
                      size="lg"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Sin cambios en 72h
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Sube un archivo para ver el resultado</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Job History */}
      {jobHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Historial de Procesamiento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {jobHistory.map((job) => (
                <div 
                  key={job.id} 
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {job.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-600" />}
                    {job.status === 'processing' && <Loader2 className="h-4 w-4 text-yellow-600 animate-spin" />}
                    {job.status === 'error' && <AlertCircle className="h-4 w-4 text-red-600" />}
                    {job.status === 'pending' && <Clock className="h-4 w-4 text-gray-400" />}
                    <div>
                      <p className="text-sm font-medium">{job.original_filename || `Job ${job.id}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(job.created_at)} • {formatFileSize(job.file_size || 0)}
                        {job.rows > 0 && ` • ${job.rows.toLocaleString()} filas`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {job.status === 'completed' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDownload(job.id)}
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDeleteJob(job.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Column Reference */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Columnas que se mantienen ({keptColumns.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {keptColumns.map(({ excel, app }) => (
                <div key={excel} className="flex items-center gap-2 bg-green-50 rounded px-2 py-1">
                  <span className="text-green-700 font-mono text-xs">{excel}</span>
                  <ArrowRight className="h-3 w-3 text-green-400" />
                  <span className="text-green-600 font-mono text-xs">{app}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Columnas que se eliminan ({removedColumns.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {removedColumns.map((col) => (
                <span 
                  key={col} 
                  className="bg-red-50 text-red-600 text-xs font-mono px-2 py-1 rounded"
                >
                  {col}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExcelCleanerTab;
